import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  ChevronRight,
  Cpu,
  FileText,
  Globe,
  Layers,
  MessageSquare,
  Rocket,
  Server,
  Sparkles,
  Terminal,
  User,
  Wrench,
  Key,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useTenant } from "@/context/tenant-context";
import { useAgent, useAgentsByCompany } from "@/hooks/use-agents";
import { agentsApi, type Agent, type CreateAgentInput } from "@/api/agents";
import { modelsApi, type ModelCatalogueItem } from "@/api/models";
import { onboardingApi } from "@/api/onboarding";
import { queryKeys } from "@/lib/query-keys";
import {
  SectionHeader,
  FieldLabel,
  Pill,
  StepWorkspace,
} from "./new-agent/steps";

type Step = 1 | 2 | 3 | 4 | 5; // 5 = success

const STEPS: Array<{ id: 1 | 2 | 3 | 4; label: string; desc: string; icon: LucideIcon }> = [
  { id: 1, label: "Identity", desc: "Name, role, manager", icon: User },
  { id: 2, label: "Workspace", desc: "Identity files", icon: FileText },
  { id: 3, label: "Model", desc: "LLM & defaults", icon: Cpu },
  { id: 4, label: "Review", desc: "Create subagent", icon: Rocket },
];

function formatCost(m: ModelCatalogueItem): string {
  const fmt = (c: number | null) =>
    c == null ? "?" : c >= 100 ? `$${(c / 100).toFixed(0)}` : `$${(c / 100).toFixed(2)}`;
  return `${fmt(m.costPer1mInput)} / ${fmt(m.costPer1mOutput)} per 1M tokens`;
}

function MiniAvatar({ size = 20 }: { size?: number }) {
  return (
    <div
      className="shrink-0 rounded-md bg-gradient-to-br from-accent to-accent-hover text-white flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <Bot size={Math.round(size * 0.55)} />
    </div>
  );
}

function StepRail({
  step,
  setStep,
  form,
  parent,
  catalogueModels,
}: {
  step: Step;
  setStep: (s: Step) => void;
  form: CreateAgentInput;
  parent: Agent | undefined;
  catalogueModels: ModelCatalogueItem[];
}) {
  const model = catalogueModels.find(
    (m) => m.modelId === (form.adapterConfig?.model as string | undefined),
  );
  return (
    <aside className="hidden h-full w-[300px] min-w-[300px] shrink-0 flex-col border-r border-border bg-sidebar px-5 py-6 sm:flex">
      <div className="mb-7 flex items-center gap-2.5">
        <Layers size={18} className="text-accent" />
        <span className="text-[15px] font-semibold">New subagent</span>
      </div>

      {parent && (
        <div className="mb-5 rounded-[10px] border border-border bg-surface p-3">
          <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
            Parent agent
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            <MiniAvatar size={22} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-text-primary">
                {parent.name}
              </div>
              {parent.title && (
                <div className="truncate text-[11px] text-text-muted">{parent.title}</div>
              )}
            </div>
          </div>
          <p className="m-0 mt-2 text-[11px] text-text-muted">
            Shares VM — no extra infrastructure cost.
          </p>
        </div>
      )}

      <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
        {STEPS.map((s, i) => {
          const done = s.id < step;
          const active = s.id === step;
          const clickable = s.id < step && step <= 4;
          const Icon = s.icon;
          return (
            <li key={s.id}>
              <button
                disabled={!clickable}
                onClick={() => clickable && setStep(s.id as Step)}
                className={`relative flex w-full items-center gap-3 rounded-[10px] border-0 bg-transparent p-2.5 text-left ${
                  active ? "bg-accent/[0.08]" : ""
                } ${clickable ? "cursor-pointer hover:bg-surface-hover" : "cursor-default"}`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-semibold transition-all ${
                    done
                      ? "bg-accent text-white"
                      : active
                        ? "text-accent"
                        : "bg-surface-hover text-text-muted"
                  }`}
                  style={{
                    backgroundColor: active && !done ? "rgba(16,163,127,0.12)" : undefined,
                  }}
                >
                  {done ? <Check size={14} strokeWidth={2.5} /> : <Icon size={14} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className={`text-[13px] font-semibold ${
                      active ? "text-accent" : "text-text-primary"
                    }`}
                  >
                    {s.label}
                  </div>
                  <div className="text-[11.5px] text-text-muted">{s.desc}</div>
                </div>
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className="ml-[22px] h-3 border-l-[1.5px]"
                  style={{
                    borderColor: done ? "var(--accent)" : "var(--border)",
                    opacity: done ? 0.4 : 1,
                  }}
                />
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-auto border-t border-border pt-5">
        <p className="mb-2.5 m-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
          So far
        </p>
        <div className="flex flex-col gap-1.5 text-[12.5px]">
          <SumRow k="Name" v={form.name || "—"} />
          <SumRow k="Role" v={form.title || form.role || "—"} />
          <SumRow k="Model" v={model?.displayName ?? "—"} />
        </div>
      </div>
    </aside>
  );
}

function SumRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-text-muted">{k}</span>
      <span className="max-w-[170px] overflow-hidden text-ellipsis whitespace-nowrap font-medium text-text-primary">
        {v}
      </span>
    </div>
  );
}

// ═════════════════ STEP 2 — MODEL ═════════════════
function StepModel({
  form,
  set,
  parent,
  catalogueModels,
}: {
  form: CreateAgentInput;
  set: (patch: Partial<CreateAgentInput>) => void;
  parent: Agent | undefined;
  catalogueModels: ModelCatalogueItem[];
}) {
  const selectedModel =
    (form.adapterConfig?.model as string | undefined) ?? "claude-sonnet-4-5";
  const parentModel = parent?.adapterConfig?.model as string | undefined;

  return (
    <div>
      <SectionHeader
        eyebrow="Step 2 of 3 · Model"
        title="Which model should this subagent use?"
        desc={`Defaults to the parent's model${parentModel ? ` (${parentModel})` : ""}. Pick something lighter for routine work, or heavier for longer-context specialisation.`}
      />

      <FieldLabel hint="Usage counts against the parent agent's monthly budget">
        Model
      </FieldLabel>
      <div className="flex flex-col gap-2">
        {catalogueModels.length === 0 && (
          <p className="text-sm text-text-muted">No models available in the catalogue yet.</p>
        )}
        {catalogueModels.map((m) => {
          const sel = selectedModel === m.modelId;
          const isParent = m.modelId === parentModel;
          return (
            <button
              key={m.id}
              onClick={() =>
                set({
                  adapterConfig: { ...(form.adapterConfig ?? {}), model: m.modelId },
                })
              }
              className={`flex items-center gap-3.5 rounded-[10px] border p-3 text-left transition-all ${
                sel
                  ? "border-accent bg-accent/[0.04] shadow-[0_0_0_3px_rgba(16,163,127,0.10)]"
                  : "border-border bg-surface hover:bg-surface-hover"
              }`}
            >
              <div
                className={`h-[18px] w-[18px] shrink-0 rounded-full ${
                  sel
                    ? "border-[5px] border-accent bg-accent shadow-[inset_0_0_0_3px_var(--background)]"
                    : "border border-border bg-background"
                }`}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13.5px] font-semibold">{m.displayName}</span>
                  <span className="text-[11px] text-text-muted">· {m.provider}</span>
                  {isParent && <Pill tone="accent">Parent's model</Pill>}
                </div>
              </div>
              <span className="font-mono text-xs text-text-muted">{formatCost(m)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════ STEP 3 — REVIEW ═════════════════
function StepReview({
  form,
  parent,
  agents,
  catalogueModels,
}: {
  form: CreateAgentInput;
  parent: Agent | undefined;
  agents: Agent[];
  catalogueModels: ModelCatalogueItem[];
}) {
  const modelId = (form.adapterConfig?.model as string | undefined) ?? "claude-sonnet-4-5";
  const model = catalogueModels.find((m) => m.modelId === modelId);
  const boss = agents.find((a) => a.id === form.reportsTo);

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex border-b border-border py-3 last:border-b-0">
      <span className="flex-[0_0_180px] text-xs font-semibold uppercase tracking-[0.05em] text-text-muted">
        {label}
      </span>
      <div className="flex-1 text-[13.5px] text-text-primary">{children}</div>
    </div>
  );

  return (
    <div>
      <SectionHeader
        eyebrow="Step 3 of 3 · Review"
        title="Ready to create this subagent?"
        desc="Subagents run on the parent's VM and are available the moment you create them — no deployment needed."
      />

      <div className="rounded-xl border border-border bg-surface px-5 py-1">
        <Row label="Identity">
          <div className="flex items-center gap-2.5">
            <MiniAvatar size={28} />
            <div>
              <div className="font-semibold">{form.name || "Unnamed subagent"}</div>
              {form.title && (
                <div className="text-xs text-text-muted">
                  {form.title}
                  {form.department ? ` · ${form.department}` : ""}
                </div>
              )}
            </div>
          </div>
        </Row>
        <Row label="Parent">
          {parent ? (
            <div className="flex items-center gap-2">
              <MiniAvatar size={20} />
              <span>{parent.name}</span>
              {parent.title && <span className="text-text-muted">· {parent.title}</span>}
            </div>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </Row>
        <Row label="Reports to">
          {boss ? (
            <>
              {boss.name}
              {boss.role && <span className="text-text-muted"> · {boss.role}</span>}
            </>
          ) : (
            "None"
          )}
        </Row>
        <Row label="Model">{model?.displayName ?? modelId}</Row>
        <Row label="Runtime">
          <span>Shares {parent?.name ?? "parent"}'s VM</span>
          <span className="mx-2 text-text-muted">·</span>
          <span className="text-text-muted">$0/mo infrastructure</span>
        </Row>
        <Row label="Capabilities">
          {form.capabilities ? (
            <p className="m-0 leading-relaxed text-text-secondary">{form.capabilities}</p>
          ) : (
            <span className="text-text-muted">Not specified</span>
          )}
        </Row>
      </div>

      <p className="mt-3.5 flex items-center gap-1.5 text-xs text-text-muted">
        <Layers size={13} /> Created instantly. No Azure work, no provisioning wait.
      </p>
    </div>
  );
}

// ═════════════════ SUCCESS ═════════════════
function SubagentReady({
  form,
  parent,
  onChat,
  onOpen,
  onBack,
}: {
  form: CreateAgentInput;
  parent: Agent | undefined;
  onChat: () => void;
  onOpen: () => void;
  onBack: () => void;
}) {
  return (
    <div className="mx-auto max-w-[560px] px-6 py-12 text-center">
      <div className="relative mx-auto mb-6 h-24 w-24">
        <div
          className="flex h-24 w-24 items-center justify-center rounded-[24px] text-white"
          style={{
            background: "linear-gradient(135deg, #10a37f 0%, #0d8a6a 100%)",
            boxShadow: "0 12px 40px rgba(16,163,127,0.3)",
          }}
        >
          <Layers size={44} />
        </div>
        <div
          className="absolute flex h-[28px] w-[28px] items-center justify-center rounded-full text-white"
          style={{
            bottom: -4,
            right: -4,
            background: "var(--accent)",
            border: "3px solid var(--background)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
        >
          <Check size={13} strokeWidth={3} />
        </div>
      </div>

      <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
        Subagent ready
      </p>
      <h1 className="m-0 mt-2 text-[28px] font-semibold tracking-[-0.02em]">
        {form.name} joined {parent?.name ?? "the agent"}
      </h1>
      <p className="m-0 mt-2.5 mb-7 text-[14.5px] leading-relaxed text-text-secondary">
        {form.title ? `${form.title}. ` : ""}Ready to take work alongside the parent agent. No deployment needed.
      </p>

      <div className="flex flex-wrap justify-center gap-2.5">
        <Button size="lg" onClick={onChat}>
          <MessageSquare size={15} />
          Start a chat
        </Button>
        <Button variant="outline" size="lg" onClick={onOpen}>
          Open subagent page
          <ArrowRight size={14} />
        </Button>
      </div>

      <div className="mt-10 rounded-xl border border-border bg-surface p-5 text-left">
        <p className="m-0 mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
          What's next
        </p>
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {[
            { Icon: Wrench, text: "Grant tools and tune capabilities" },
            { Icon: Key, text: "Share secrets or MCP access with this subagent" },
            { Icon: User, text: `Delegate tasks from ${parent?.name ?? "the parent"}` },
          ].map((it, i) => (
            <li key={i} className="flex items-center gap-3 text-[13px] text-text-secondary">
              <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-surface-hover text-text-muted">
                <it.Icon size={14} />
              </div>
              {it.text}
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={onBack}
        className="mt-7 border-0 bg-transparent text-[12.5px] text-text-muted underline"
      >
        Back to {parent?.name ?? "parent"}
      </button>
    </div>
  );
}

// ═════════════════ Main ═════════════════
export function NewSubagent({
  embedded = false,
  parentAgentId: parentAgentIdProp,
  onClose,
  onCreated,
}: {
  embedded?: boolean;
  /** Embedded: the parent agent id (instead of the route param). */
  parentAgentId?: string;
  onClose?: () => void;
  onCreated?: (agentId: string) => void;
} = {}) {
  useDocumentTitle("New subagent");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { agentId: routeParentId } = useParams<{ agentId: string }>();
  const parentAgentId = parentAgentIdProp ?? routeParentId;
  const { data: parentAgent } = useAgent(parentAgentId ?? "");
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const { data: catalogueModels = [] } = useQuery({
    queryKey: queryKeys.models.all,
    queryFn: modelsApi.list,
  });
  const { data: onboarding } = useQuery({
    queryKey: ["onboarding", tenantId],
    queryFn: () => onboardingApi.getStatus(tenantId),
  });
  const departments = onboarding?.departments ?? [];

  const [step, setStep] = useState<Step>(1);
  const [createdAgent, setCreatedAgent] = useState<Agent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<CreateAgentInput>({
    companyId: tenantId,
    name: "",
    role: "ic",
    title: "",
    department: null,
    reportsTo: parentAgentId ?? null,
    capabilities: "",
    adapterConfig: { model: "claude-sonnet-4-5" },
  });

  // Inherit parent's model once loaded (only if user hasn't picked yet)
  const [modelInherited, setModelInherited] = useState(false);
  useEffect(() => {
    if (!modelInherited && parentAgent?.adapterConfig?.model) {
      setForm((f) => ({
        ...f,
        adapterConfig: {
          ...(f.adapterConfig ?? {}),
          model: parentAgent.adapterConfig?.model as string,
        },
      }));
      setModelInherited(true);
    }
  }, [parentAgent, modelInherited]);

  // Runtime choice only exists under an OpenClaw parent: the subagent can be
  // born directly on the ProClaw Runtime (side-by-side, recommended) or as a
  // classic OpenClaw persona. Other parents: subagent inherits, no picker.
  const runtimeChoosable = parentAgent?.adapterType === "openclaw_gateway";
  // Native parents pass their runtime flavour down (adapterConfig spread on the
  // server). A non-claude-code Native parent can also birth a subagent onto the
  // side-by-side Claude Code runtime; a claude-code parent just passes it down.
  const parentRuntimeMode = (parentAgent?.adapterConfig as Record<string, unknown> | undefined)
    ?.runtimeMode as string | undefined;
  const nativeRuntimeChoosable =
    parentAgent?.adapterType === "anthropic_native" && parentRuntimeMode !== "claude-code";
  const inheritedRuntimeLabel =
    parentAgent?.adapterType === "anthropic_native" && parentRuntimeMode === "claude-code"
      ? "Native — Claude Code (persistent Claude Code session per chat, tenant OAuth)"
      : null;

  interface RuntimeOption {
    id: string;
    adapterType: string;
    runtimeMode?: string;
    label: string;
    icon: LucideIcon;
    desc: string;
    recommended?: boolean;
  }
  const claudeCodeOption: RuntimeOption = {
    id: "claude_code_native",
    adapterType: "anthropic_native",
    runtimeMode: "claude-code",
    label: "Native — Claude Code",
    icon: Terminal,
    desc: "Real Claude Code, side-by-side on the parent's VM, using the tenant's Claude OAuth token. Each chat links to a persistent Claude Code session.",
  };
  const runtimeOptions: RuntimeOption[] = runtimeChoosable
    ? [
        {
          id: "proclaw_runtime",
          adapterType: "proclaw_runtime",
          label: "ProClaw Runtime",
          icon: Server,
          desc: "Born on the owned runtime (side-by-side). Foundry-ready, broker-authed. Recommended.",
          recommended: true,
        },
        {
          id: "openclaw_gateway",
          adapterType: "openclaw_gateway",
          label: "OpenClaw persona",
          icon: Globe,
          desc: "Classic persona on the parent's OpenClaw gateway.",
        },
        claudeCodeOption,
      ]
    : nativeRuntimeChoosable
      ? [
          {
            id: "inherit_native",
            adapterType: "anthropic_native",
            label: "Anthropic Native (inherit)",
            icon: Sparkles,
            desc:
              parentRuntimeMode === "agent-sdk"
                ? "Share the parent's Agent SDK runtime (tenant OAuth). Recommended."
                : "Share the parent's direct-SDK runtime. Recommended.",
            recommended: true,
          },
          claudeCodeOption,
        ]
      : [];
  const runtimePickerVisible = runtimeOptions.length > 0;
  const [runtimeDefaulted, setRuntimeDefaulted] = useState(false);
  useEffect(() => {
    if (!runtimeDefaulted && runtimePickerVisible) {
      setForm((f) => ({
        ...f,
        adapterType: f.adapterType ?? runtimeOptions[0].adapterType,
      }));
      setRuntimeDefaulted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimePickerVisible, runtimeDefaulted]);

  const set = (patch: Partial<CreateAgentInput>) =>
    setForm((f) => ({ ...f, ...patch }));

  const createMutation = useMutation({
    mutationFn: () =>
      agentsApi.createSubagent(parentAgentId!, {
        name: form.name,
        role: form.role,
        title: form.title || undefined,
        department: form.department || undefined,
        reportsTo: form.reportsTo,
        capabilities: form.capabilities || undefined,
        adapterConfig: form.adapterConfig,
        ...(runtimePickerVisible && form.adapterType ? { adapterType: form.adapterType } : {}),
        ...(form.workspace ? { workspace: form.workspace } : {}),
      }),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
      setCreatedAgent(agent);
      setStep(5);
    },
    onError: (err: Error) => {
      setError(err.message ?? "Failed to create subagent.");
    },
  });

  const canProceed = useMemo(() => {
    if (step === 1) return form.name.trim().length > 0;
    if (step === 3) return !!form.adapterConfig?.model;
    return true;
  }, [step, form.name, form.adapterConfig?.model]);

  const goNext = () => {
    if (step < 4) setStep((step + 1) as Step);
    else if (step === 4) createMutation.mutate();
  };
  const goBack = () => {
    if (step > 1 && step <= 4) setStep((step - 1) as Step);
  };

  if (!parentAgentId) return null;
  const showRail = step <= 4;
  const parentHref = `/tenants/${tenantId}/agents/${parentAgentId}`;
  // Embedded (settings dialog) leaves via callbacks instead of routing.
  const closeWizard = () => {
    if (embedded) onClose?.();
    else navigate(parentHref);
  };
  const openCreated = () => {
    if (!createdAgent) return;
    if (embedded) onCreated?.(createdAgent.id);
    else navigate(`/tenants/${tenantId}/agents/${createdAgent.id}?tab=config`);
  };

  return (
    <div className="flex h-full w-full bg-background">
      {showRail && (
        <StepRail
          step={step}
          setStep={setStep}
          form={form}
          parent={parentAgent}
          catalogueModels={catalogueModels}
        />
      )}

      <main className="flex flex-1 flex-col overflow-hidden">
        {showRail && (
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-7">
            <div className="flex items-center gap-2.5 text-[13px] text-text-muted">
              <button
                onClick={() =>
                  embedded ? onClose?.() : navigate(`/tenants/${tenantId}/agents`)
                }
                className="border-0 bg-transparent text-text-muted hover:text-text-primary"
              >
                Agents
              </button>
              <ChevronRight size={12} />
              <button
                onClick={closeWizard}
                className="border-0 bg-transparent text-text-muted hover:text-text-primary"
              >
                {parentAgent?.name ?? "Agent"}
              </button>
              <ChevronRight size={12} />
              <span className="font-medium text-text-primary">New subagent</span>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {showRail ? (
            <div className="mx-auto max-w-[820px] px-8 pt-9 pb-[120px]">
              {step === 1 && (
                <>
                  <SectionHeader
                    eyebrow="Step 1 of 4 · Identity"
                    title={`Who joins ${parentAgent?.name ?? "this agent"}?`}
                    desc="Give the subagent a name, a role, and what it specialises in. Shared parent VM means zero infra setup."
                  />
                  {/* Reuse the identity form fields — skip StepIdentity's own section header */}
                  <IdentityFields
                    form={form}
                    set={set}
                    agents={agents}
                    departments={departments}
                  />
                  {runtimePickerVisible && (
                    <div className="mt-6">
                      <FieldLabel hint="All options run on the parent's VM — no new infrastructure">
                        Runtime
                      </FieldLabel>
                      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                        {runtimeOptions.map((r) => {
                          const Icon = r.icon;
                          const selected =
                            form.adapterType === r.adapterType &&
                            ((form.adapterConfig?.runtimeMode as string | undefined) ?? undefined) ===
                              r.runtimeMode;
                          return (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => {
                                const nextConfig = { ...(form.adapterConfig ?? {}) } as Record<
                                  string,
                                  unknown
                                >;
                                if (r.runtimeMode) nextConfig.runtimeMode = r.runtimeMode;
                                else delete nextConfig.runtimeMode;
                                set({ adapterType: r.adapterType, adapterConfig: nextConfig });
                              }}
                              className={`rounded-[10px] border p-3.5 text-left transition-all ${
                                selected
                                  ? "border-accent bg-accent/[0.04] shadow-[0_0_0_3px_rgba(16,163,127,0.10)]"
                                  : "border-border bg-surface hover:bg-surface-hover"
                              }`}
                            >
                              <div className="mb-1.5 flex items-center gap-2.5">
                                <Icon
                                  size={18}
                                  className={selected ? "text-accent" : "text-text-secondary"}
                                />
                                <span className="text-[13.5px] font-semibold text-text-primary">
                                  {r.label}
                                </span>
                                {r.recommended && <Pill tone="accent">Recommended</Pill>}
                              </div>
                              <p className="m-0 text-xs leading-snug text-text-muted">{r.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!runtimePickerVisible && inheritedRuntimeLabel && (
                    <div className="mt-6">
                      <FieldLabel hint="Subagents run on the parent's VM and runtime — no new infrastructure">
                        Runtime
                      </FieldLabel>
                      <div className="rounded-[10px] border border-border bg-surface p-3.5">
                        <span className="text-[13.5px] font-semibold text-text-primary">
                          {inheritedRuntimeLabel}
                        </span>
                        <p className="m-0 mt-1 text-xs leading-snug text-text-muted">
                          Inherited from {parentAgent?.name ?? "the parent agent"}.
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
              {step === 2 && (
                <StepWorkspace
                  form={form}
                  set={set}
                  companyId={tenantId}
                  eyebrow="Step 2 of 4 · Workspace"
                  title="Review the subagent's workspace files"
                  desc="Generated now so the subagent lands ready to work — no waiting after creation. Edit anything, or regenerate."
                />
              )}
              {step === 3 && (
                <StepModel
                  form={form}
                  set={set}
                  parent={parentAgent}
                  catalogueModels={catalogueModels}
                />
              )}
              {step === 4 && (
                <StepReview
                  form={form}
                  parent={parentAgent}
                  agents={agents}
                  catalogueModels={catalogueModels}
                />
              )}

              {step === 4 && error && (
                <div className="mt-4 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
                  {error}
                </div>
              )}
            </div>
          ) : (
            <SubagentReady
              form={form}
              parent={parentAgent}
              onChat={() =>
                createdAgent &&
                (embedded
                  ? onCreated?.(createdAgent.id)
                  : navigate(`/tenants/${tenantId}/agents/${createdAgent.id}?tab=chat`))
              }
              onOpen={openCreated}
              onBack={closeWizard}
            />
          )}
        </div>

        {showRail && (
          <div className="sticky bottom-0 flex shrink-0 items-center justify-between border-t border-border bg-background px-8 py-3.5">
            <Button
              variant="ghost"
              onClick={goBack}
              className={step === 1 ? "invisible" : ""}
            >
              <ArrowLeft size={14} /> Back
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-muted">{step} of 4</span>
              {step < 4 ? (
                <Button onClick={goNext} disabled={!canProceed}>
                  Continue <ArrowRight size={14} />
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={goNext}
                  disabled={createMutation.isPending}
                >
                  <Layers size={14} />
                  {createMutation.isPending ? "Creating…" : "Create subagent"}
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Identity form without its own section header — the step title lives outside.
function IdentityFields({
  form,
  set,
  agents,
  departments,
}: {
  form: CreateAgentInput;
  set: (patch: Partial<CreateAgentInput>) => void;
  agents: Agent[];
  departments: string[];
}) {
  const deptOptions = departments.length
    ? departments
    : ["IT", "Finance", "People", "Strategy", "Operations", "Engineering", "Sales", "Support"];

  return (
    <div className="grid max-w-[680px] grid-cols-1 gap-4 md:grid-cols-2">
      <div className="md:col-span-2">
        <FieldLabel hint="How people will @mention them">Subagent name</FieldLabel>
        <input
          placeholder="e.g. Legal Analyst"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          className="h-[38px] w-full rounded-lg border border-border bg-background px-3 text-sm text-text-primary outline-none focus:border-accent"
        />
      </div>

      <div>
        <FieldLabel>Title</FieldLabel>
        <input
          placeholder="e.g. Contract Specialist"
          value={form.title ?? ""}
          onChange={(e) => set({ title: e.target.value })}
          className="h-[38px] w-full rounded-lg border border-border bg-background px-3 text-sm text-text-primary outline-none focus:border-accent"
        />
      </div>

      <div>
        <FieldLabel>Department</FieldLabel>
        <select
          value={form.department ?? ""}
          onChange={(e) => set({ department: e.target.value || null })}
          className="h-[38px] w-full rounded-lg border border-border bg-background px-3 text-sm text-text-primary"
        >
          <option value="">Unassigned</option>
          {deptOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-2">
        <FieldLabel hint="Defaults to the parent agent">Reports to</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => set({ reportsTo: null })}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-all ${
              form.reportsTo == null
                ? "border-accent bg-accent/[0.08] text-text-primary"
                : "border-border bg-surface text-text-primary hover:bg-surface-hover"
            }`}
          >
            <span>None (top-level)</span>
          </button>
          {agents.map((a) => {
            const selected = form.reportsTo === a.id;
            return (
              <button
                key={a.id}
                onClick={() => set({ reportsTo: a.id })}
                className={`inline-flex items-center gap-2 rounded-full border py-1.5 pl-2 pr-3 text-[12.5px] font-medium transition-all ${
                  selected
                    ? "border-accent bg-accent/[0.08] text-text-primary"
                    : "border-border bg-surface text-text-primary hover:bg-surface-hover"
                }`}
              >
                <MiniAvatar size={20} />
                <span>{a.name}</span>
                {a.role && (
                  <span className="text-[11.5px] text-text-muted">· {a.role}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="md:col-span-2">
        <FieldLabel hint="One or two sentences. Helps the subagent introduce itself.">
          Capabilities
        </FieldLabel>
        <textarea
          value={form.capabilities ?? ""}
          onChange={(e) => set({ capabilities: e.target.value })}
          placeholder="What does this subagent specialise in?"
          className="min-h-[92px] w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-[13.5px] leading-relaxed text-text-primary outline-none focus:border-accent"
        />
      </div>
    </div>
  );
}
